// Onboarding Model - Main data layer hook
// Fetches all onboarding data in parallel and exposes mutation actions

import {
  useGetOnboardingQuestionsQuery,
  useGetOnboardingResponsesQuery,
  useGetOnboardingDismissalQuery,
  useGetStrategiesQuery,
  useGetStrategyScoringCategoriesQuery,
  useGetStrategyScoringRulesQuery,
  useSaveOnboardingResponsesMutation,
  useSaveSelectedStrategyMutation,
  useSaveOnboardingDismissalMutation,
  useResetOnboardingMutation,
  useClearSelectedStrategyMutation,
  useGetBookingStepLazyQuery,
  Platform,
} from 'generated/graphql';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import { Async } from '@client/new/helpers/types';
import { makeAsync } from '@client/new/helpers/async';
import { resultsToAsync } from '@client/new/helpers/gql';
import {
  OnboardingQuestion,
  OnboardingResponse,
  OnboardingDismissal,
  Strategy,
  ScoringCategory,
  ScoringRule,
  BookingEligibility,
} from './Onboarding.types';
import {
  mapQuestion,
  mapResponses,
  toOnboardingDismissal,
  mapStrategies,
  mapScoringCategories,
  mapScoringRules,
  toBookingEligibility,
} from './Onboarding.mappers';

// Model data shape.
export type OnboardingModelData = {
  questions: OnboardingQuestion[];
  responses: OnboardingResponse[];
  dismissal: OnboardingDismissal;
  strategies: Strategy[];
  scoringCategories: ScoringCategory[];
  scoringRules: ScoringRule[];
};

// Model actions
export type OnboardingModelActions = {
  saveResponses: (responses: OnboardingResponse[]) => Promise<void>;
  saveStrategy: (strategyId: string) => Promise<void>;
  clearStrategy: () => Promise<void>;
  dismiss: () => Promise<void>;
  refetchResponses: () => Promise<void>;
  resetOnboarding: () => Promise<void>;
  // Resolves the creator's booking classification at strategy selection.
  // `pending` means enrichment hasn't resolved yet — the caller should wait
  // and re-check; only a resolved verdict routes to book-call or the guide.
  checkBookingEligibility: () => Promise<BookingEligibility>;
};

// Full model type
export type OnboardingModel = OnboardingModelData & OnboardingModelActions;

export const useOnboardingModel = (): Async<OnboardingModel> => {
  const creatorId = useInfluencerId();
  const creatorIdStr = String(creatorId);

  // Fetch all data in parallel
  const questionsQuery = useGetOnboardingQuestionsQuery({
    variables: { platform: Platform.Web },
  });

  const responsesQuery = useGetOnboardingResponsesQuery({
    variables: { creatorId: creatorIdStr },
  });

  const dismissalQuery = useGetOnboardingDismissalQuery({
    variables: { creatorId: creatorIdStr },
  });

  const strategiesQuery = useGetStrategiesQuery({
    variables: { creatorId: creatorIdStr },
  });

  const categoriesQuery = useGetStrategyScoringCategoriesQuery();

  const rulesQuery = useGetStrategyScoringRulesQuery();

  // Mutations
  const [saveResponsesMutation] = useSaveOnboardingResponsesMutation();
  const [saveStrategyMutation] = useSaveSelectedStrategyMutation();
  const [clearStrategyMutation] = useClearSelectedStrategyMutation();
  const [saveDismissalMutation] = useSaveOnboardingDismissalMutation();
  const [resetOnboardingMutation] = useResetOnboardingMutation();
  const [fetchBookingStep] = useGetBookingStepLazyQuery({
    fetchPolicy: 'network-only',
  });

  // Check if all queries are ready
  const combinedState = resultsToAsync([
    questionsQuery,
    responsesQuery,
    dismissalQuery,
    strategiesQuery,
    categoriesQuery,
    rulesQuery,
  ]);

  // If not all fetched, return the combined state (loading/error)
  if (combinedState.type !== 'fetched') {
    return combinedState;
  }

  // Extract and map data
  const scoringCategories = categoriesQuery.data?.GetStrategyScoringCategory
    ? mapScoringCategories(categoriesQuery.data.GetStrategyScoringCategory)
    : [];

  const rawQuestions = questionsQuery.data?.GetOnboardingQuestions ?? [];
  const questions = rawQuestions.map(mapQuestion);

  const responses = responsesQuery.data?.GetOnboardingResponses
    ? mapResponses(responsesQuery.data.GetOnboardingResponses)
    : [];

  const dismissal = toOnboardingDismissal(
    dismissalQuery.data?.GetOnboardingDismissal,
  );

  const strategies = strategiesQuery.data?.GetStrategies
    ? mapStrategies(strategiesQuery.data.GetStrategies)
    : [];

  const scoringRules = rulesQuery.data?.GetStrategyScoringRules
    ? mapScoringRules(rulesQuery.data.GetStrategyScoringRules)
    : [];

  // Action: Save responses. Sends `answerIds` (canonical) along with the
  // legacy `answerId` derived from `answerIds[0]` for back-compat with the
  // older server contract until the server stops reading `answerId`.
  const saveResponses = async (
    newResponses: OnboardingResponse[],
  ): Promise<void> => {
    const input = newResponses.map((r) => ({
      questionId: r.questionId,
      answerId: r.answerIds[0] ?? null,
      answerIds: r.answerIds,
      textValue: r.textValue,
    }));

    const result = await saveResponsesMutation({
      variables: {
        creatorId: creatorIdStr,
        responses: input,
      },
    });

    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    await Promise.all([responsesQuery.refetch(), strategiesQuery.refetch()]);
  };

  // Action: Save selected strategy
  const saveStrategy = async (strategyId: string): Promise<void> => {
    const result = await saveStrategyMutation({
      variables: {
        creatorId: creatorIdStr,
        strategyId,
      },
    });

    if (result.errors) {
      throw new Error(result.errors[0].message);
    }
  };

  // Action: Clear selected strategy (for "skip for now")
  const clearStrategy = async (): Promise<void> => {
    const result = await clearStrategyMutation({
      variables: { creatorId: creatorIdStr },
    });
    if (result.errors) {
      throw new Error(result.errors[0].message);
    }
  };

  // Action: Dismiss onboarding
  const dismiss = async (): Promise<void> => {
    const result = await saveDismissalMutation({
      variables: {
        creatorId: creatorIdStr,
        platform: Platform.Web,
      },
    });

    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    await dismissalQuery.refetch();
  };

  // Action: Refetch responses (for use after navigation)
  const refetchResponses = async (): Promise<void> => {
    await responsesQuery.refetch();
  };

  // Action: Resolve booking-step classification for the current creator.
  const checkBookingEligibility = async (): Promise<BookingEligibility> => {
    const result = await fetchBookingStep({
      variables: { creatorId: creatorIdStr },
    });
    return toBookingEligibility(result.data?.GetBookingStep?.status);
  };

  // Action: Reset onboarding (for testing on integration env)
  const resetOnboarding = async (): Promise<void> => {
    const result = await resetOnboardingMutation({
      variables: { creatorId: creatorIdStr },
    });

    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    // Refetch all data to get clean state
    await Promise.all([
      responsesQuery.refetch(),
      dismissalQuery.refetch(),
      strategiesQuery.refetch(),
    ]);
  };

  return makeAsync.fetched({
    questions,
    responses,
    dismissal,
    strategies,
    scoringCategories,
    scoringRules,
    saveResponses,
    saveStrategy,
    clearStrategy,
    dismiss,
    refetchResponses,
    resetOnboarding,
    checkBookingEligibility,
  });
};
